// Face sign-in: the station-key /face calls (jobq-api "face sign-in", contract 2026-10-15).
//
// Station routes carry this browser's station key as X-Face-Station (src/face/faceStation.js).
// Only /face calls carry it: config.js's global fetch wrapper never adds it.
// The Admin routes (X-Admin-Pin) are in faceAdminApi.js: they go through utils/securityApi.js,
// which loads config.js, and this file is loaded by every PIN prompt (see faceFetch.js).
//
// Every helper resolves {ok, status, body, message} and never throws.

import { faceFetch } from './faceFetch';
import { clearStation, getStationKey, getStationStatus } from './faceStation';

export { getStationStatus };

const NO_KEY = { ok: false, status: 401, body: null, message: 'This computer is not a face sign-in station.', noKey: true };

/**
 * A station-key call. The key is cleared only on the API's own "not a face sign-in station"
 * 401: a wrong PIN (enroll, remove) or an API-key 401 during a deploy must never unregister a
 * station.
 */
async function stationFetch(path, opts = {}) {
  const key = getStationKey();
  if (!key) return NO_KEY;
  const r = await faceFetch(path, {
    ...opts,
    headers: { ...(opts.headers || {}), 'X-Face-Station': key },
  });
  if (r.status === 401 && /station/i.test(r.message || '')) {   // never on another 401 (wrong PIN, API key)
    clearStation();
    r.stationGone = true;
  }
  return r;
}

const post = (body) => ({ method: 'POST', body: JSON.stringify(body || {}) });

// ---- station routes ----

/** POST /face/identify with one base64 JPEG (no data: prefix needed). */
export function identifyFace(image, { signal } = {}) {
  return stationFetch('/face/identify', { ...post({ image }), ...(signal ? { signal } : {}) });
}

/**
 * The identify answer as the PIN prompt reads it:
 *   {outcome: 'matched', token, user_id, name, similarity}
 *   {outcome: 'no_face' | 'multiple_faces' | 'no_match' | 'ambiguous' | 'disabled' | 'unavailable', message}
 *   {outcome: 'error', message}   (no answer, 4xx/5xx; message null unless the API said why)
 */
export function readIdentify(r) {
  if (r && r.ok && r.body && typeof r.body === 'object') {
    const b = r.body;
    if (b.outcome === 'matched' && b.token) {
      return {
        outcome: 'matched',
        token: String(b.token),
        user_id: b.user_id != null ? b.user_id : null,
        name: b.name || '',
        similarity: typeof b.similarity === 'number' ? b.similarity : null,
      };
    }
    return { outcome: b.outcome || 'error', message: b.message || null };
  }
  // 429 / 400 carry a sentence worth showing; a network error or 5xx falls back quietly.
  const say = r && (r.status === 429 || r.status === 400) ? r.message : null;
  return { outcome: 'error', message: say, status: r ? r.status : 0, stationGone: Boolean(r && r.stationGone) };
}

/** POST /face/not_me: revokes the token ("Not you?"). Fire and forget. */
export function faceNotMe(token) {
  return stationFetch('/face/not_me', post({ token }));
}

/** POST /face/enroll with the person's OWN PIN and 3-6 base64 JPEGs. */
export function enrollFace(pin, images) {
  return stationFetch('/face/enroll', post({ pin, images }));
}

/** POST /face/enrollment/remove with the person's own PIN. */
export function removeMyFace(pin) {
  return stationFetch('/face/enrollment/remove', post({ pin }));
}
